/**
 * Reads `text/html` and `text/plain` off the system clipboard for the paths
 * that have no `ClipboardEvent` to read from — a menu item, or a chord that
 * fires as a `keydown` (SYD-2314).
 *
 * Returns the `{ getData }` shape `parseClipboard` consumes, or `null` when the
 * browser refused access, which every caller reports as blocked rather than as
 * an empty clipboard. Consumed by `useArTableClipboard.pasteRows` and
 * `ArRowContextMenu`'s Paste Cell; the native `onPaste` path reads
 * `e.clipboardData` directly and does not come through here.
 */

type SystemClipboardPayload = { getData: (type: string) => string };

async function readSystemClipboard(): Promise<SystemClipboardPayload | null> {
  try {
    const items = await navigator.clipboard.read();
    const readType = async (mime: string): Promise<string> => {
      for (const item of items) {
        if (item.types.includes(mime)) {
          return (await item.getType(mime)).text();
        }
      }
      return '';
    };
    const html = await readType('text/html');
    const plain = await readType('text/plain');
    return { getData: (type) => (type === 'text/html' ? html : type === 'text/plain' ? plain : '') };
  } catch {
    return null;
  }
}

export { readSystemClipboard };
export type { SystemClipboardPayload };
